import { createPortal } from 'react-dom';
import { usePageTitle, useHeaderActionsTarget } from '@/layouts/usePageTitle';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import ManagementTablePanel from '@/components/common/ManagementTablePanel';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BarChart3,
  Brain,
  Building,
  ChevronDown,
  ChevronUp,
  Home,
  Mail,
  Phone,
  Save,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  colorSchemeLightCold,

  themeQuartz,
} from 'ag-grid-community';
import type { CellClickedEvent, ColDef } from 'ag-grid-community';
import toast from 'react-hot-toast';
import {
  fetchInvestorDetail,
  updateInvestor,
  deleteInvestor,
  type Investor,
  type InvestorPlant,
} from '@/api/investors';
import { useI18n } from '@/i18n';
import InvestorLogo from '@/components/investor/InvestorLogo';
import InvestorAIProviderSelect from '@/components/investor/InvestorAIProviderSelect';
import DropdownMenu from '@/ui/common/DropdownMenu';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';

const modules = [AllEnterpriseModule];

const gridTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

const formatProduction = (value: unknown) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const amount = Number(value);
  const magnitude = Math.abs(amount);
  const divisor = magnitude >= 1_000_000 ? 1_000_000 : magnitude >= 1000 ? 1000 : 1;
  const unit = divisor === 1_000_000 ? 'GWh' : divisor === 1000 ? 'MWh' : 'kWh';
  return `${(amount / divisor).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
};
const productionColumn: ColDef<InvestorPlant> = {
  type: 'numericColumn',
  cellDataType: 'number',
  cellClass: 'ag-right-aligned-cell tabular-nums',
  headerClass: 'ag-right-aligned-header',
  valueFormatter: ({ value }) => formatProduction(value),
  filterParams: { valueLabel: formatProduction },
};

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border bg-white p-3">
      <div className="mt-0.5 text-gray-600">{icon}</div>
      <div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="text-sm font-semibold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: number }) {
  const { t } = useI18n();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {status === 1 ? t('investors.active') : t('investors.passive')}
    </span>
  );
}

interface InvestorFormData {
  investor_name: string;
  manager_name: string;
  manager_surname: string;
  email: string;
  phone: string;
  address: string;
  subdomain: string;
  ai_provider:'gemini'|'openai';
  status: number;
  logo_url: string | null;
}

function InvestorEditModal({
  investor,
  onClose,
  onSave,
}: {
  investor: Investor;
  onClose: () => void;
  onSave: (data: InvestorFormData) => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<InvestorFormData>({
    investor_name: investor.investor_name || '',
    manager_name: investor.manager_name || '',
    manager_surname: investor.manager_surname || '',
    email: investor.email || '',
    phone: investor.phone || '',
    address: investor.address || '',
    subdomain: investor.subdomain || '',
    ai_provider:investor.ai_provider || 'gemini',
    status: investor.status,
    logo_url: investor.logo_url || '',
  });

  const set = (key: keyof InvestorFormData, value: string | number | null) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="text-lg font-semibold text-gray-900">{t('investors.editTitle')}</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mb-6 flex justify-center mt-4">
            <InvestorLogo 
              uuidId={investor.investor_uuid_id} 
              initialLogoUrl={form.logo_url} 
              onLogoChange={(url) => set('logo_url', url)}
            />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.name')}</label>
            <input
              type="text"
              value={form.investor_name}
              onChange={(e) => set('investor_name', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.managerName')}</label>
            <input
              type="text"
              value={form.manager_name}
              onChange={(e) => set('manager_name', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.managerSurname')}</label>
            <input
              type="text"
              value={form.manager_surname}
              onChange={(e) => set('manager_surname', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.email')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.phone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">Subdomain</label>
            <input
              type="text"
              value={form.subdomain}
              onChange={(e) => set('subdomain', e.target.value)}
              placeholder="ornek-yatirimci"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
            <p className="mt-1 text-xs text-gray-400">Örn: ornek-yatirimci.pvsense.local</p>
          </div>
          <InvestorAIProviderSelect value={form.ai_provider} onChange={value=>set('ai_provider',value)}/>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('investors.address')}</label>
            <textarea
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-500">{t('common.status')}</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => set('status', 1)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.status === 1
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('investors.active')}
              </button>
              <button
                type="button"
                onClick={() => set('status', 0)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  form.status === 0
                    ? 'border-rose-500 bg-rose-50 text-rose-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {t('investors.passive')}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
          >
            {t('investors.cancel')}
          </button>
          <button
            onClick={() => onSave(form)}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700"
          >
            <Save size={16} />
            {t('investors.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function InvestorHeaderCard({ investor, onLogoChange }: { investor: Investor, onLogoChange: (url: string | null) => void }) {
  const { t } = useI18n();
  const [showContacts, setShowContacts] = useState(false);

  return (
    <div className="rounded-2xl bg-white p-4 shadow-md sm:p-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="flex shrink-0 items-center gap-3 sm:flex-col">
          <InvestorLogo
            key={investor.investor_uuid_id}
            uuidId={investor.investor_uuid_id}
            initialLogoUrl={investor.logo_url}
            onLogoChange={onLogoChange}
          />
          <StatusBadge status={investor.status} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <User className="text-gray-600" size={18} />
            <p className="text-sm font-medium text-gray-800">
              {investor.investor_manager_fullname || t('investors.unknownManager')}
            </p>
          </div>

          <div className="mt-4 sm:hidden">
            <button
              onClick={() => setShowContacts((prev) => !prev)}
              className="flex w-full items-center justify-between rounded-lg bg-gray-100 p-2 text-sm font-medium transition hover:bg-gray-200"
            >
              <span>{t('investors.contactDetails')}</span>
              {showContacts ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>

          <div
            className={`mt-4 grid grid-cols-1 gap-3 transition-all duration-300 sm:grid-cols-2 ${
              showContacts
                ? 'max-h-[1000px] opacity-100'
                : 'max-h-0 overflow-hidden opacity-0 sm:max-h-none sm:opacity-100'
            }`}
          >
            <InfoRow icon={<Mail size={16} />} label={t('investors.email')} value={investor.email || '—'} />
            <InfoRow icon={<Phone size={16} />} label={t('investors.phone')} value={investor.phone || '—'} />
            <InfoRow icon={<Home size={16} />} label={t('investors.address')} value={investor.address || '—'} />
            <InfoRow icon={<Building size={16} />} label="Subdomain" value={investor.subdomain || '—'} />
            <InfoRow icon={<Brain size={16} />} label="AI Sağlayıcısı" value={investor.ai_provider==='openai'?'OpenAI':'Google Gemini'} />
            <InfoRow icon={<Building size={16} />} label={t('investors.plantCount')} value={String(investor.plant_count)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PlantStatusBadge({ status }: { status: number }) {
  const { t } = useI18n();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
        status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {status === 1 ? t('common.online') : t('common.offline')}
    </span>
  );
}

export default function InvestorDetailPage() {
  const { t } = useI18n();
  const { uuid_id } = useParams<{ uuid_id: string }>();
  const navigate = useNavigate();

  const [investor, setInvestor] = useState<Investor | null>(null);
  const [plants, setPlants] = useState<InvestorPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [editOpen, setEditOpen] = useState(false);
  const [search, setSearch] = useState('');
  const headerActionsTarget = useHeaderActionsTarget();
  usePageTitle(investor?.investor_name);

  const openDeleteConfirm = async () => {
    if (!(await confirmToast(t('investors.deleteConfirmTitle'), t('investors.deleteConfirmMessage'))) || !uuid_id) return;
    try {
      await deleteInvestor(uuid_id);
      toast.success(t('investors.deleteSuccess'));
      navigate('/dashboard/investor');
    } catch {
      toast.error(t('investors.deleteError'));
    }
  };

  useEffect(() => {
    if (!uuid_id) return;
    fetchInvestorDetail(uuid_id)
      .then((data) => {
        setInvestor(data.investor);
        setPlants(data.plants);
      })
      .catch(() => setError(t('investors.detailLoadError')))
      .finally(() => setLoading(false));
  }, [uuid_id, t]);

  const totalPlantPower = useMemo(() => plants.reduce((acc, p) => acc + (p.power_plant_status === 1 ? 1 : 0), 0), [plants]);
  const colDefs = useMemo<ColDef<InvestorPlant>[]>(
    () => [
      {
        field: 'power_plant_name',
        headerName: t('sensors.powerPlant'),
        flex: 2,
        minWidth: 180,
        pinned: 'left',
        filter: ColumnValuesFilter,
      },
      {
        ...productionColumn,
        field: 'power_plant_e_day',
        headerName: t('common.eDay'),
        flex: 1,
        minWidth: 120,
        filter: ColumnValuesFilter,
      },
      {
        ...productionColumn,
        field: 'power_plant_e_total',
        headerName: t('common.eTotal'),
        flex: 1,
        minWidth: 120,
        filter: ColumnValuesFilter,
      },
      {
        field: 'power_plant_last_seen',
        headerName: t('common.lastSeen'),
        flex: 1.5,
        minWidth: 160,
        filter: ColumnValuesFilter,
        valueFormatter: ({ value }: { value: string | null }) => value || '—',
      },
      {
        field: 'power_plant_status',
        headerName: t('common.status'),
        width: 120,
        filter: ColumnValuesFilter,
        filterParams: {
          valueLabel: (value: unknown) =>
            Number(value) === 1 ? t('common.online') : t('common.offline'),
        },
        cellRenderer: ({ value }: { value: number }) => (
          <PlantStatusBadge status={value} />
        ),
      },
    ],
    [t],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      floatingFilter: false,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  const menuItems = investor ? [
    {
      label: t('investors.edit'),
      icon: <Save size={14} />,
      variant: 'info' as const,
      onClick: () => setEditOpen(true),
    },
    {
      label: 'Raporlar',
      icon: <BarChart3 size={14} />,
      variant: 'default' as const,
      onClick: () => {},
      children: [
        {
          label: 'Günlük Yatırımcı Raporu',
          icon: <BarChart3 size={14} />,
          variant: 'default' as const,
          onClick: () => navigate('/dashboard/reports', {
            state: {
              prefillReportId: 'investor-daily',
              prefillInvestorUuid: investor.investor_uuid_id,
            },
          }),
        },
        {
          label: 'Aylık Yatırımcı Raporu',
          icon: <BarChart3 size={14} />,
          variant: 'default' as const,
          onClick: () => navigate('/dashboard/reports', {
            state: {
              prefillReportId: 'investor-monthly',
              prefillInvestorUuid: investor.investor_uuid_id,
            },
          }),
        },
        {
          label: 'Bütçe vs. Gerçekleşen Üretim',
          icon: <BarChart3 size={14} />,
          variant: 'default' as const,
          onClick: () => navigate('/dashboard/reports', {
            state: {
              prefillReportId: 'plant-budget-vs-actual',
              prefillInvestorUuid: investor.investor_uuid_id,
            },
          }),
        },
      ],
    },
    {
      label: t('investors.delete'),
      icon: <Trash2 size={14} />,
      variant: 'danger' as const,
      onClick: openDeleteConfirm,
      className: plants.length > 0 ? 'opacity-40 pointer-events-none' : '',
    },
  ] : [];

  if (error) return <LoadRetry />;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto pb-4">
      {headerActionsTarget && investor && createPortal(
        <div title={plants.length > 0 ? t('investors.cannotDeleteWithPlants') : undefined}>
          <DropdownMenu title={t('management.users.actions')} items={menuItems} align="right" />
        </div>, headerActionsTarget)}

      {loading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-gray-100" />
      ) : investor ? (
        <InvestorHeaderCard 
          investor={investor} 
          onLogoChange={(url) => setInvestor(prev => prev ? { ...prev, logo_url: url } : null)} 
        />
      ) : null}

      {editOpen && investor && (
        <InvestorEditModal
          investor={investor}
          onClose={() => setEditOpen(false)}
          onSave={async (data) => {
            if (!uuid_id) return;
            try {
              await updateInvestor(uuid_id, data);
              setInvestor({
                ...investor,
                ...data,
                investor_manager_fullname: `${data.manager_name} ${data.manager_surname}`.trim() || null,
              });
              setEditOpen(false);
              toast.success(t('investors.saveSuccess'));
            } catch {
              toast.error(t('investors.saveError'));
            }
          }}
        />
      )}


      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">{t('investors.powerPlants')}</h2>
        {!loading && (
          <span className="text-sm text-gray-400">
            {t('investors.totalOnline', { total: plants.length, online: totalPlantPower })}
          </span>
        )}
      </div>

      <section className="flex h-[500px] shrink-0 flex-col">
      <ManagementTablePanel search={search} onSearch={setSearch} label="Yatırımcı santrallerinde ara" count={plants.length}>
      <div className="min-h-0 flex-1">
        <AgGridReact<InvestorPlant>
          modules={modules}
          theme={gridTheme}
          rowData={loading ? null : plants}
          quickFilterText={search}
          getRowId={params => params.data.power_plant_uuid_id}
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          animateRows
          rowStyle={{ cursor: 'pointer' }}
          onCellClicked={(e: CellClickedEvent<InvestorPlant>) => {
            if (e.data?.power_plant_uuid_id) {
              navigate(`/dashboard/powerplants/${e.data.power_plant_uuid_id}`);
            }
          }}
          enableCellTextSelection
        />
      </div>
      </ManagementTablePanel>
      </section>
    </div>
  );
}
